import { cloneDeep } from 'lodash'
import { useApplicationCallbacks } from '../../../application-callbacks'
import { useModalsDataActions } from '../../../../hooks/useModalsDataActions'
import useStateControls from '../../../../hooks/useStateControls'
import { useOnboardingConditions } from './useOnboardingConditions'
import {
  ONBOARDING_BUTTON_CALLBACKS,
  ONBOARDING_BUTTONS_CONDITION,
  ONBOARDING_IMAGE_CONDITIONS,
  ONBOARDING_MODAL_TYPES,
  ONBOARDING_STEPS_TYPES,
} from '../../constants'
import getOnboardingModals from '../../config/modals'
import { gtmFunctions } from '../../../../constants/analytics/GTM'
import { defaultContur, conturs, defaultLanguage, languages } from '../../../../translations'
import { CHALLENGES as CHALLENGES_STATE } from '../../../../constants/shared/states/names'
import { ACHIEVEMENTS as ACHIEVEMENTS_TAB } from '../../../../components/pages/challenges/constants/tabs'
import { useService } from '../../../../redux/reducer/service'
import { useUser } from '../../../../redux/reducer/user'

export const useOnboardingModal = () => {
  const { addModal } = useModalsDataActions()
  const { setState } = useStateControls()
  const { serviceData } = useService()
  const { profile } = useUser()
  const { isNewUser } = useOnboardingConditions()
  const onboardingModals = getOnboardingModals(profile?.username)
  const {
    setCurrentOnboardingStep,
    setOnboardingStarted,
    setCompletedStepsData,
    addCurrentStepToCompleted,
    resetOnboardingState,
    setUserModalsVisibility,
    setChallengesActiveTab,
  } = useApplicationCallbacks()

  const customActions = {
    [ONBOARDING_BUTTON_CALLBACKS.START]: () => {
      setCompletedStepsData([])
      setOnboardingStarted(true)
      addCurrentStepToCompleted()
      openModal(ONBOARDING_STEPS_TYPES.ACTIVITY)
    },
    [ONBOARDING_BUTTON_CALLBACKS.UPDATE]: () => addCurrentStepToCompleted(),
    [ONBOARDING_BUTTON_CALLBACKS.RESET]: () => {
      setCompletedStepsData(onboardingModals.map((modal) => modal.id))
      resetOnboardingState()
    },
    [ONBOARDING_BUTTON_CALLBACKS.SHOW_USER_MODALS]: () => {
      globalThis.setTimeout(() => {
        setUserModalsVisibility(true)
      }, 500)
    },
    [ONBOARDING_BUTTON_CALLBACKS.SHOW_ROADMAP]: () => {
      setState('roadmap')
      gtmFunctions['GTMRoadmap']?.()
    },
    [ONBOARDING_BUTTON_CALLBACKS.SHOW_ACHIEVEMENTS_TAB]: () => {
      setState(CHALLENGES_STATE)
      setChallengesActiveTab(ACHIEVEMENTS_TAB)
    },
  }

  const isImageConditionMatched = (condition) => {
    const isMeContur = defaultContur === conturs.me
    const currentLanguage = serviceData?.lang || defaultLanguage
    const cisLanguages = [languages.ru, languages.kz]
    const isRuLanguage = cisLanguages.includes(currentLanguage)

    switch (condition) {
      case ONBOARDING_IMAGE_CONDITIONS.HIDE_SECTIONS_2026_ME:
        return isMeContur
      case ONBOARDING_IMAGE_CONDITIONS.HIDE_SECTIONS_2026_CIS:
        return !isMeContur
      case ONBOARDING_IMAGE_CONDITIONS.HIDE_SECTIONS_2026_ME_RU:
        return isMeContur && isRuLanguage
      case ONBOARDING_IMAGE_CONDITIONS.HIDE_SECTIONS_2026_ME_INTL:
        return isMeContur && !isRuLanguage
      default:
        return false
    }
  }

  const resolveConditionalImage = (defaultImage, imageByCondition = []) =>
    imageByCondition.find(({ condition }) => isImageConditionMatched(condition))?.image ||
    defaultImage

  const setModalProps = (modal) => {
    const baseProps = { ...modal.props }

    if (modal.type === ONBOARDING_MODAL_TYPES.SLIDE_MODAL) {
      const slides = cloneDeep(modal.props.slides).map((slide) => {
        const { imgByCondition, ...restSlide } = slide
        return {
          ...restSlide,
          img: resolveConditionalImage(restSlide.img, imgByCondition),
        }
      })

      slides[1].buttons[0].attr.customCallbacks = customActions
      return { ...baseProps, slides }
    }

    const { imageByCondition, ...restProps } = baseProps
    const resolvedProps = {
      ...restProps,
      image: resolveConditionalImage(restProps.image, imageByCondition),
    }

    let buttons = resolvedProps.buttons.map((button) => ({
      ...button,
      attr: { ...button.attr, customCallbacks: customActions },
    }))

    // Для новых пользователей скрываем возможность пропустить онбординг
    if (isNewUser && modal.id === ONBOARDING_STEPS_TYPES.START) {
      buttons = buttons.filter(
        (button) => button.attr?.condition !== ONBOARDING_BUTTONS_CONDITION.NOT_NEW_USER,
      )
    }

    return { ...resolvedProps, buttons }
  }

  const openModal = (type) => {
    const modal = onboardingModals.find((modal) => modal.id === type)

    if (!modal) return

    setCurrentOnboardingStep(type)

    addModal({
      type: modal.type,
      props: setModalProps(modal),
    })
  }

  return { openOnboardingModal: openModal }
}
